import qs from 'qs'
import React from 'react'
import moment from 'moment'
import useSWR from 'swr'
import { merge } from 'lodash'
import { isSameDay } from 'date-fns'
import { Backdrop, CircularProgress, Divider, useTheme } from '@mui/material'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import { useHistory, useLocation } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { DateCalendar } from '@mui/x-date-pickers'
import { track } from '@SonarCX/lib/mixpanel'

import WidgetContainer from '@SonarCX/components/atoms/WidgetContainer'
import Checkbox from '../../Settings/Controls/Checkbox'
import Event from './components/Event'

import { Routes } from '@SonarCX/Routes'
import {
  parseDatetimeValue,
  parseInclude
} from '../../Tasks/Tabs/AllTasksTab/helpers/parse'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import {
  editReminder,
  loadRemindersByDateRange,
  selectReminder
} from '@SonarCX/slices/reminder'
import { generateRecurrentEvents } from './helpers/remindersHelper'
import useSavedValue from '@SonarCX/hooks/useSavedValue'

import useStyles from './styles'

const localizer = momentLocalizer(moment)

const PlannerTab = () => {
  const now = moment().toDate()
  const history = useHistory()
  const location = useLocation()
  const classes = useStyles()
  const theme = useTheme()
  const params = qs.parse(location.search, { ignoreQueryPrefix: true })
  const { eventId } = params
  const dispatch = useAppDispatch()
  const hasPermission = useAccess()
  const {
    reminders,
    loading: remindersLoading,
    loaded: remindersLoaded
  } = useAppSelector(selectReminder)
  const { setIDs } = useTaskList()
  const { user, loaded: envLoaded } = useEnvironment()
  const [selectedDate, setSelectedDate] = useSavedValue<Date>({
    key: 'dashboard_planner_date_range',
    defaultValue: `"${now.toISOString()}"`,
    useSession: true
  })
  const [includeSubTasks, setIncludeSubTasks] = useSavedValue<boolean>({
    key: 'dashboard_planner_show_subtasks',
    defaultValue: 'true',
    useSession: true
  })

  const startOfMonth = React.useMemo(
    () => moment(selectedDate).startOf('month').toDate().toISOString(),
    [selectedDate]
  )
  const endOfMonth = React.useMemo(
    () => moment(selectedDate).endOf('month').toDate().toISOString(),
    [selectedDate]
  )

  const canViewTasks = React.useMemo(
    () => hasPermission([PERMS.TasksOwn, PERMS.TasksAudit]),
    [hasPermission]
  )
  const canLoad = React.useMemo(
    () => envLoaded && Boolean(user?.id),
    [envLoaded, user?.id]
  )

  const {
    data = [],
    error,
    isLoading
  } = useSWR(() => {
    if (!canLoad) return
    let params = {}

    params = merge(params, parseDatetimeValue(startOfMonth, 'due_start'))
    params = merge(params, parseDatetimeValue(endOfMonth, 'due_end'))
    params = merge(params, parseInclude(includeSubTasks))
    let result = qs.stringify(params, { arrayFormat: 'brackets' })

    return `planner:${result}:${user?.id}`
  })

  //more stuff can be added
  const taskEvents = React.useMemo(() => {
    return data.map(t => ({
      id: t.id,
      title: '',
      longTitle: t.title,
      start: t.due_date ? new Date(t.due_date) : new Date(),
      end: t.due_date ? new Date(t.due_date) : new Date(),
      allDay: true,
      isSubtask: t.parent_id,
      isParent: t.sub_tasks > 0,
      type: 'task'
    }))
  }, [data])

  const reminderEvents = React.useMemo(() => {
    const allEvents = generateRecurrentEvents(reminders, endOfMonth)
    return allEvents.map(r => ({
      id: r.id,
      title: '',
      longTitle: r.title,
      start: r.stamp ? new Date(r.stamp) : new Date(),
      end: r.stamp ? new Date(r.stamp) : new Date(),
      type: 'reminder',
      objectType: r.object_type
    }))
  }, [endOfMonth, reminders])

  const handleCalendarPickerClick = React.useCallback(
    val => {
      setSelectedDate(val.toDate())
    },
    [setSelectedDate]
  )

  const handleSelectSlot = React.useCallback(
    ({ start, end }) => {
      const currentDateTime = moment()
      // Subtract one day from the current date
      const yesterday = currentDateTime.clone().subtract(1, 'day')
      // we dont create reminders on the past!
      if (moment(start).isSameOrBefore(yesterday, 'day')) {
        return
      }
      track('PLANNER_SELECT_SLOT')
      const startDate = moment(start).toDate()
      startDate.setHours(currentDateTime.hours())
      startDate.setMinutes(currentDateTime.minutes() + 5)
      const reminder = {
        id: '',
        title: '',
        details: '',
        stamp: startDate.toISOString(),
        object_id: null,
        object_type: null,
        user_id: '',
        alerts: [
          {
            channel_id: null,
            minutes_before: 5
          }
        ],
        repeat: null
      }

      dispatch(editReminder(reminder))
    },
    [dispatch]
  )

  const handleEventClick = React.useCallback(
    calEvent => {
      if (calEvent.type === 'reminder') {
        const reminder = reminders.find(r => r.id === calEvent.id)
        if (reminder) {
          track('PLANNER_OPEN_REMINDER')
          dispatch(editReminder(reminder))
          return
        }
        dispatch(AddErrorAlert({ err: { message: 'Reminder not found' } }))
        return
      }

      // Find tasks on same day and group and save
      const onSameDay = taskEvents
        .filter(event => isSameDay(event.start, calEvent.start))
        .sort((a, b) => a.start - b.start)
        .map(event => event.id)
      setIDs(onSameDay, calEvent.id)

      history.push(`${Routes.TASKS}/${calEvent.id}`)
    },
    [dispatch, taskEvents, setIDs, history, reminders]
  )

  const eventPropGetter = React.useCallback(
    event => {
      let newStyle = {
        backgroundColor: theme.palette.primary.main
      }

      if (event.type === 'reminder') {
        newStyle.backgroundColor = theme.palette.altColor
      }

      return {
        style: newStyle
      }
    },
    [theme.palette.altColor, theme.palette.primary.main]
  )

  React.useEffect(() => {
    if (error) {
      dispatch(AddErrorAlert({ err: { message: 'Error getting tasks!' } }))
      console.error('error getting tasks for the planner', error)
    }
  }, [dispatch, error])

  //load reminders every month change.
  React.useEffect(() => {
    if (canLoad) {
      try {
        dispatch(loadRemindersByDateRange(startOfMonth, endOfMonth))
      } catch (err) {
        dispatch(
          AddErrorAlert({ err: { message: 'Error getting reminders!' } })
        )
        console.error('error loading reminders for the planner', err)
      }
    }
  }, [dispatch, startOfMonth, endOfMonth, canLoad])

  React.useEffect(() => {
    if (!eventId || !remindersLoaded) return

    const reminder = reminders.find(r => r.id === eventId)
    if (reminder) {
      track('PLANNER_OPEN_REMINDER')
      dispatch(editReminder(reminder))
      history.replace(`${Routes.DASHBOARD}`)
      return
    }
    dispatch(AddErrorAlert({ err: { message: 'Reminder not found' } }))
    history.replace(`${Routes.DASHBOARD}`)

    // We dont need this to run every time the reminders change, only when the ID and the reminders have been loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, eventId, history, remindersLoaded])

  return (
    <>
      <WidgetContainer
        variant='header'
        visible={canViewTasks}
        className={classes.header}
      >
        <Checkbox checked={includeSubTasks} onCheck={setIncludeSubTasks}>
          Show subtasks
        </Checkbox>
      </WidgetContainer>

      <WidgetContainer
        variant='body'
        visible={canViewTasks}
        className={classes.body}
      >
        <div className={classes.layout}>
          <div className='side-nav'>
            <div>
              <DateCalendar
                value={moment(selectedDate)}
                onChange={handleCalendarPickerClick}
                views={['day']}
              />
            </div>
            <Divider />
            <div className={classes.legendContanier}>
              <div>
                <div className={classes.dot} />
                &nbsp;Task
              </div>
              <div>
                <div className={classes.reminderDot} />
                &nbsp;Reminder
              </div>
            </div>
          </div>

          <div className={classes.calendar}>
            <Calendar
              popup
              resizable
              selectable
              defaultDate={now}
              date={selectedDate}
              defaultView='month'
              views={['month']}
              events={[...taskEvents, ...reminderEvents]}
              localizer={localizer}
              onNavigate={setSelectedDate}
              onSelectEvent={handleEventClick}
              onSelectSlot={handleSelectSlot}
              components={{
                event: Event
              }}
              eventPropGetter={eventPropGetter}
            />
          </div>
        </div>
      </WidgetContainer>

      <Backdrop
        open={isLoading || remindersLoading}
        className={classes.backdrop}
      >
        <CircularProgress color={'primary'} />
      </Backdrop>
    </>
  )
}

export default PlannerTab
